import React, { useContext, useState } from 'react'
import Text from '@embracesbs/component-text'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'
import { formatDateTime } from '../../Utilities/helpers/helpers'
import { useConfig } from '../Config/ConfigContext'
import { useSubscriptions } from '../Context/SubscriptionContext'
import { useTenants } from '../Context/TenantContext'
import { AuthRoleContext } from '../Auth/AuthRoleContext'
import DeleteSubscriptionModal from './DeleteSubscriptionModal'

// Mirrors SanitizeFolderName in AzureDevopsService.cs. Azure DevOps splits a pipeline
// folder path on '/' and '\' and rejects any segment ending in '.' or ' ', so those
// characters are folded into the folder name rather than left to create segments.
// Customer names keep their '/'; only the derived folder differs, so the deep link
// below has to fold identically or it points at a folder that does not exist.
const sanitizeFolderName = (name: string) => {
  const folded = name.replace(/[/\\:*?"<>|]/g, '-')
  // Trim '.' and ' ' together: removing trailing dots alone can re-expose a trailing space.
  return folded.trim().replace(/[. ]+$/, '') || 'unnamed'
}

interface SubscriptionExtendedDetailsProbs {}

const SubscriptionExtendedDetails: React.FC<SubscriptionExtendedDetailsProbs> = ({}) => {
  const config = useConfig()
  const roles = useContext(AuthRoleContext)
  const { selectedSubscription, removeSubscription } = useSubscriptions()
  const { selectedTenant, tenants, loading: tenantsLoading } = useTenants()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  if (selectedSubscription === null || selectedTenant !== null) {
    return <></>
  }

  // Mirrors the backend pipelineBaseFolder: subscriptions live in
  // "1. Tenant Management Application (TMA)/Application Triggered/<NonProd|Prod>/<customerName>".
  const environments = config?.environments || []
  const pipelineEnvFolder = environments.some((env) => ['beta', 'production'].includes(env)) ? 'Prod' : 'NonProd'
  const definitionScope = `\\1. Tenant Management Application (TMA)\\Application Triggered\\${pipelineEnvFolder}\\${sanitizeFolderName(selectedSubscription.customerName)}`
  const pipelineUrl = `https://dev.azure.com/embrace/Embrace%20Suite/_build?definitionScope=${encodeURIComponent(definitionScope)}`

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--tma-page-bg)',
          margin: '0 -20px 0 -40px',
          padding: '0 20px 14px 40px'
        }}
      >
        <div style={{ height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Text textStyle='h6' style={{ fontSize: '16px' }}>
              Subscription Properties
            </Text>
          </div>
          <div style={{ borderTop: '1px solid #bcbcc2', marginLeft: '-40px', marginRight: '-20px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '14px' }}>
          <Button2
            style={{ width: '100%', justifyContent: 'center' }}
            buttonSize='30px'
            buttonRadius='8px'
            variant='outlined'
            leftElement={<Icon iconName='ConstructionPipe1' size={16} />}
            text='Subscription Pipeline'
            onClick={() => window.open(pipelineUrl, '_blank', 'noopener,noreferrer')}
          />
          {roles?.isAdmin && (
            <>
              <Button2
                style={{ width: '100%', justifyContent: 'center' }}
                buttonSize='30px'
                buttonRadius='8px'
                variant='outlined'
                isDanger
                isDisabled={tenantsLoading || tenants.length > 0}
                leftElement={<Icon iconName='Delete1' size={16} />}
                text='Delete subscription'
                onClick={() => setDeleteModalOpen(true)}
              />
              {tenants.length > 0 && (
                <Text textColor='grey' textStyle='description' content={`Delete its ${tenants.length} tenant(s) first.`} />
              )}
            </>
          )}
        </div>
      </div>
      <Text textColor='grey' textStyle='description'>
        Name:
      </Text>
      <Text textStyle='body'>{selectedSubscription.customerName}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        Number of tenants:
      </Text>
      <Text textStyle='body'>{tenants.length}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        CreatedBy:
      </Text>
      <Text textStyle='body'>{selectedSubscription.createdBy}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        CreationTime:
      </Text>
      <Text textStyle='body'>{formatDateTime(selectedSubscription.creationTime)}</Text>
      <br></br>
      {selectedSubscription.modifiedBy && (
        <>
          <Text textColor='grey' textStyle='description'>
            LastModifiedBy:
          </Text>
          <Text textStyle='body'>{selectedSubscription.modifiedBy}</Text>
          <br />
        </>
      )}
      <Text textColor='grey' textStyle='description'>
        InternalId:
      </Text>
      <Text textStyle='body'>{selectedSubscription.id}</Text>
      {deleteModalOpen && (
        <DeleteSubscriptionModal
          isOpen={deleteModalOpen}
          subscription={selectedSubscription}
          onClosed={() => setDeleteModalOpen(false)}
          onDeleted={() => removeSubscription(selectedSubscription.id)}
        />
      )}
    </>
  )
}

export default SubscriptionExtendedDetails
